import Immutable from 'immutable';
import * as actionTypes from './actionTypes';
import { STATUS } from './constants';

const defaultState = Immutable.fromJS({
  lessons: {},
  coursesByLessonId: {},
  quizStatsLoadingByLessonId: {},
  quizStatsByQuestionId: {},
  quizAttemptsByLessonId: {},
  quizAttemptSavingByQuestionId: {},
  chunkVisitsByLessonId: {},
  chunkVisitSaving: {},
  successMeasureResponsesByLessonId: {},
  successMeasureResponsesSaving: {},
});

/*
  These updaters perform a consistent updating of state where
  data is stored in a map behind an item key with fields
  'status', 'failureReason', and 'data'
  Anything other than loading/reloading that data from api
  needs to use a custom update function.
*/
export const requestUpdater = (state, reducerKey, itemKey) => {
  return state.getIn([reducerKey, `${itemKey}`])
    ? state.updateIn([reducerKey, `${itemKey}`], (lesson) =>
        lesson.set('status', STATUS.LOADING).set('failureReason', '')
      )
    : state.update(reducerKey, (items) =>
        items.set(
          `${itemKey}`,
          Immutable.fromJS({
            status: STATUS.LOADING,
            failureReason: '',
          })
        )
      );
};

export const successUpdater = (state, reducerKey, itemKey, data) => {
  return state.setIn(
    [reducerKey, `${itemKey}`],
    Immutable.fromJS({
      status: STATUS.READY,
      failureReason: '',
      data,
    })
  );
};

export const failureUpdater = (state, reducerKey, itemKey, failureReason) => {
  return state.setIn(
    [reducerKey, `${itemKey}`],
    Immutable.fromJS({
      status: STATUS.FAILURE,
      failureReason,
    })
  );
};

export default (state = defaultState, { type, payload }) => {
  switch (type) {
    case actionTypes.LOAD_LESSON_BY_LESSON_ID_REQUEST:
      return requestUpdater(state, 'lessons', payload.lessonId);

    case actionTypes.LOAD_LESSON_BY_LESSON_ID_SUCCESS:
      return successUpdater(state, 'lessons', payload.lessonId, payload.lesson);

    case actionTypes.LOAD_LESSON_BY_LESSON_ID_FAILURE:
      return failureUpdater(
        state,
        'lessons',
        payload.lessonId,
        payload.failureReason
      );

    case actionTypes.LOAD_COURSE_BY_LESSON_ID_REQUEST:
      return requestUpdater(state, 'coursesByLessonId', payload.lessonId);

    case actionTypes.LOAD_COURSE_BY_LESSON_ID_SUCCESS:
      return successUpdater(
        state,
        'coursesByLessonId',
        payload.lessonId,
        payload.course
      );

    case actionTypes.LOAD_COURSE_BY_LESSON_ID_FAILURE:
      return failureUpdater(
        state,
        'coursesByLessonId',
        payload.lessonId,
        payload.failureReason
      );

    case actionTypes.LOAD_QUIZ_STATS_BY_LESSON_ID_REQUEST:
      return requestUpdater(
        state,
        'quizStatsLoadingByLessonId',
        payload.lessonId
      );

    case actionTypes.LOAD_QUIZ_STATS_BY_LESSON_ID_SUCCESS:
      return (
        successUpdater(
          state,
          'quizStatsLoadingByLessonId',
          payload.lessonId,
          payload.quizStats
        )
          // for conveniece quiz stats are also normalised to questionId
          .update('quizStatsByQuestionId', (quizStatsByQuestionId) =>
            quizStatsByQuestionId.merge(
              payload.quizStats.reduce(
                (acc, item) =>
                  acc.set(
                    `${item.get('question_id')}`,
                    item.set('lesson_id', Number(payload.lessonId)) // easily lookup status via question
                  ),
                Immutable.Map()
              )
            )
          )
      );

    case actionTypes.LOAD_QUIZ_STATS_BY_LESSON_ID_FAILURE:
      return failureUpdater(
        state,
        'quizStatsLoadingByLessonId',
        payload.lessonId,
        payload.failureReason
      );

    case actionTypes.LOAD_QUIZ_ATTEMPTS_BY_LESSON_ID_REQUEST:
      return requestUpdater(state, 'quizAttemptsByLessonId', payload.lessonId);

    case actionTypes.LOAD_QUIZ_ATTEMPTS_BY_LESSON_ID_SUCCESS:
      return successUpdater(
        state,
        'quizAttemptsByLessonId',
        payload.lessonId,
        payload.quizAttempts
      );

    case actionTypes.LOAD_QUIZ_ATTEMPTS_BY_LESSON_ID_FAILURE:
      return failureUpdater(
        state,
        'quizAttemptsByLessonId',
        payload.lessonId,
        payload.failureReason
      );

    case actionTypes.SAVE_QUIZ_ATTEMPT_REQUEST:
      return state.setIn(
        ['quizAttemptSavingByQuestionId', `${payload.questionId}`],
        Immutable.Map({
          status: STATUS.LOADING,
        })
      );

    case actionTypes.SAVE_QUIZ_ATTEMPT_SUCCESS:
      return state
        .updateIn(
          ['quizAttemptsByLessonId', `${payload.lessonId}`, 'data'],
          (quizAttempts) => quizAttempts.push(payload.quizAttempt)
        )
        .removeIn(['quizAttemptSavingByQuestionId', `${payload.questionId}`]);

    case actionTypes.SAVE_QUIZ_ATTEMPT_FAILURE:
      return state.setIn(
        ['quizAttemptSavingByQuestionId', `${payload.questionId}`],
        Immutable.Map({
          status: STATUS.FAILURE,
          failureReason: payload.failureReason,
        })
      );

    case actionTypes.LOAD_CHUNK_VISITS_BY_LESSON_ID_REQUEST:
      return requestUpdater(state, 'chunkVisitsByLessonId', payload.lessonId);

    case actionTypes.LOAD_CHUNK_VISITS_BY_LESSON_ID_SUCCESS:
      return successUpdater(
        state,
        'chunkVisitsByLessonId',
        payload.lessonId,
        payload.chunkVisits
      );

    case actionTypes.LOAD_CHUNK_VISITS_BY_LESSON_ID_FAILURE:
      return failureUpdater(
        state,
        'chunkVisitsByLessonId',
        payload.lessonId,
        payload.failureReason
      );

    case actionTypes.RECORD_CHUNK_VISIT_REQUEST:
      return requestUpdater(state, 'chunkVisitSaving', payload.chunkId);

    case actionTypes.RECORD_CHUNK_VISIT_SUCCESS:
      return state
        .updateIn(
          ['chunkVisitsByLessonId', `${payload.lessonId}`, 'data'],
          (chunkVisits) => {
            const chunkVisitIndex = chunkVisits.findIndex(
              (visit) => visit.get('chunk') === payload.chunkId
            );
            return chunkVisitIndex > -1
              ? chunkVisits.set(chunkVisitIndex, payload.chunkVisit)
              : chunkVisits.push(payload.chunkVisit);
          }
        )
        .removeIn(['chunkVisitSaving', `${payload.chunkId}`]);

    case actionTypes.RECORD_CHUNK_VISIT_FAILURE:
      return failureUpdater(
        state,
        'chunkVisitSaving',
        payload.chunkId,
        payload.failureReason
      );

    // reducers to load success measures
    case actionTypes.LOAD_SUCCESS_MEASURE_RESPONSES_BY_LESSON_ID_REQUEST:
      return requestUpdater(
        state,
        'successMeasureResponsesByLessonId',
        payload.lessonId
      );

    case actionTypes.LOAD_SUCCESS_MEASURE_RESPONSES_BY_LESSON_ID_SUCCESS:
      return successUpdater(
        state,
        'successMeasureResponsesByLessonId',
        payload.lessonId,
        payload.successMeasureResponses
      );

    case actionTypes.LOAD_SUCCESS_MEASURE_RESPONSES_BY_LESSON_ID_FAILURE:
      return failureUpdater(
        state,
        'successMeasureResponsesByLessonId',
        payload.lessonId,
        payload.failureReason
      );

    // reducers to record success measure responses
    case actionTypes.RECORD_SUCCESS_MEASURE_RESPONSE_REQUEST:
      return requestUpdater(
        state,
        'successMeasureResponsesSaving',
        payload.successMeasureId
      );

    case actionTypes.RECORD_SUCCESS_MEASURE_RESPONSE_SUCCESS:
      return state
        .updateIn(
          ['successMeasureResponsesByLessonId', `${payload.lessonId}`, 'data'],
          (successMeasureResponses) => {
            const successMeasureResponsesIndex =
              successMeasureResponses.findIndex(
                (successMeasureResponse) =>
                  successMeasureResponse.get('success_measure_id') ===
                    payload.successMeasureId &&
                  successMeasureResponse.get('profile_id') === payload.profileId
              );

            return successMeasureResponsesIndex > -1
              ? successMeasureResponses.set(
                  successMeasureResponsesIndex,
                  payload.successMeasureResponse
                )
              : successMeasureResponses.push(payload.successMeasureResponse);
          }
        )
        .setIn(
          ['successMeasureResponsesSaving', `${payload.successMeasureId}`],
          Immutable.fromJS({
            status: STATUS.READY,
            failureReason: '',
          })
        );

    case actionTypes.RECORD_SUCCESS_MEASURE_RESPONSE_FAILURE:
      return failureUpdater(
        state,
        'successMeasureResponsesSaving',
        payload.successMeasureId,
        payload.failureReason
      );

    default:
      return state;
  }
};
